import { type GetApiAlbumCollectionsQueryParams } from '@/shared/api'
import { combine, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import {
  albumCollectionCreate,
  albumCollectionDelete,
  albumCollectionUpdate,
} from '../api'
import {
  type AlbumCollectionsQueryModelWithPagination,
  type AlbumCollectionsSortState,
  createAlbumCollectionsQueryModel,
} from '../lib/album-collections-query-model'

interface AlbumCollectionsTableGateProps {
  initialFilters?: GetApiAlbumCollectionsQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
  initialSort?: AlbumCollectionsSortState
}
export const AlbumCollectionsTableGate =
  createGate<AlbumCollectionsTableGateProps>()

export const AlbumCollectionsTableAlbumCollectionsQueryModel =
  createAlbumCollectionsQueryModel({
    usePagination: true,
  }) as AlbumCollectionsQueryModelWithPagination
const { availableSet, unavailableSet } =
  AlbumCollectionsTableAlbumCollectionsQueryModel
export const {
  //   Query
  query: albumCollectionsTableQuery,
  //   Init
  $filters: $albumCollectionsTableFilters,
  filtersInit: albumCollectionsTableFiltersInit,
  //   Search
  $search: $albumCollectionsTableSearch,
  searchChanged: albumCollectionsTableSearchChanged,
  searchReset: albumCollectionsTableSearchReset,
  //   Sort
  $sort: $albumCollectionsTableSort,
  sortChanged: albumCollectionsTableSortChanged,
  sortReset: albumCollectionsTableSortReset,
  //   Pagination
  $page: $albumCollectionsTablePage,
  pageChanged: albumCollectionsTablePageChanged,
  pageReset: albumCollectionsTablePageReset,
  $pageSize: $albumCollectionsTablePageSize,
  pageSizeChanged: albumCollectionsTablePageSizeChanged,
  //   Refresh
  refreshed: albumCollectionsTableRefreshed,
} = AlbumCollectionsTableAlbumCollectionsQueryModel

sample({
  clock: AlbumCollectionsTableGate.open,
  target: availableSet,
})

// Search input

export const $albumCollectionsTableSearchInputValue = createStore('')
  .on(albumCollectionsTableSearchChanged, (_, q) => q)
  .reset(albumCollectionsTableSearchReset)

// Setup initial params
// Page
sample({
  clock: AlbumCollectionsTableGate.open,
  source: combine(
    AlbumCollectionsTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: albumCollectionsTablePageChanged,
})
// Page Size
sample({
  clock: AlbumCollectionsTableGate.open,
  source: combine(
    AlbumCollectionsTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: albumCollectionsTablePageSizeChanged,
})
// Search
sample({
  clock: AlbumCollectionsTableGate.open,
  source: combine(
    AlbumCollectionsTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: albumCollectionsTableSearchChanged,
})
// Sort
sample({
  clock: AlbumCollectionsTableGate.open,
  source: combine(
    AlbumCollectionsTableGate.state,
    ({ initialSort }) => initialSort || null,
  ),
  filter: (sort) => sort !== null,
  target: albumCollectionsTableSortChanged,
})
// Filters
sample({
  source: AlbumCollectionsTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: albumCollectionsTableFiltersInit,
})

sample({
  clock: AlbumCollectionsTableGate.close,
  target: unavailableSet,
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $albumCollectionsTablePage,
    queryData: albumCollectionsTableQuery.$data,
  }),
  clock: albumCollectionDelete.finished.success,
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: albumCollectionsTablePageChanged,
})

sample({
  clock: [
    albumCollectionCreate.finished.success,
    albumCollectionUpdate.finished.success,
    albumCollectionDelete.finished.success,
  ],
  target: albumCollectionsTableRefreshed,
})
